import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { timesheetAppDisplayName } from '../../timesheet-final/models/timesheet.models';
import { resolveBillableFromTaskPayload } from '../../timesheet-final/services/timesheet.service';
import {
  resolvePmTaskMilestoneDescription,
  resolvePmTaskMilestoneName,
  resolvePmTaskPhaseDescription,
  resolvePmTaskPhaseName,
} from '../utils/pm-entry-task-meta.util';
import { apiUrls, getProjectMembersStatusUrl } from '../../../shared/constants/api.constants';
import type {
  ProjectTimesheetUnreportedEntriesResponse,
  ProjectTimesheetUnreportedEntry,
} from '../../../services/timesheet-management/timesheet-service';
import type {
  ApiApproveRequest,
  ApiApproveResponse,
  ApiAssignedTask,
  ApiAssignedTasksResponse,
  ApiEditTimesheetRequest,
  ApiEditTimesheetResponse,
  ApiEmployee,
  ApiEmployeeListResponse,
  ReportingManagerByRangeParsed,
  ReportingManagerProjectRowDto,
  ReportingManagerProjectSlotDto,
  ApiRejectRequest,
  ApiRejectResponse,
  ApiTimesheetEntriesResponse,
  ApiTimesheetEntry,
  ApiUnreportedApp,
  ApiUnreportedAppsResponse,
  ApprovalStatus,
  ManagerTimesheetSubmitterDto,
  ManagerTimesheetsByRangeResponse,
  PendingApprovalPostBody,
  PendingApprovalTimesheetMapped,
  ProjectMemberStatusRowDto,
  ProjectMembersStatusApiResponse,
  ApprovalStatusByRangeApiResponse,
  ProjectTimesheetApprovalStatusByRangeBody,
  SupportProjectTimesheetApprovalStatusByRangeBody,
  TimesheetApprovalMutationResult,
  ProjectTimesheetManagerReviewBody,
  ProjectTimesheetSubmitTrailResponse,
  ProjectTimesheetSubmitTrailRow,
} from '../models/pm-timesheet.models';

const API_BASE = '/api/v1/timesheets/pm';

function approvalMessageFromBody(raw: unknown): string {
  if (raw == null || typeof raw !== 'object') return '';
  const rec = raw as Record<string, unknown>;
  const msg = rec['message'] ?? rec['Message'] ?? rec['error'] ?? rec['Error'];
  return String(msg ?? '').trim();
}

function mapApprovalStatusByRangeResponse(
  res: ApprovalStatusByRangeApiResponse | null | undefined,
): TimesheetApprovalMutationResult {
  const success = !!res?.success;
  const message = approvalMessageFromBody(res) || (success ? 'Request completed successfully.' : 'Request failed.');
  return { success, message };
}

function mapApprovalStatusByRangeHttpError(err: unknown): TimesheetApprovalMutationResult {
  const fromBody = approvalMessageFromBody((err as { error?: unknown })?.error);
  const message = fromBody || 'Request failed.';
  return { success: false, message };
}

function unreportedCreatedAtTimeLabel(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function mapProjectUnreportedRowToPmApp(row: ProjectTimesheetUnreportedEntry): ApiUnreportedApp {
  const appLabel = timesheetAppDisplayName(row.appId);
  const remark = (row.remark ?? '').trim();
  const taskNm = (row.taskName ?? '').trim();
  const placeholderRemark = remark === '' || remark === 'NA' || remark === 'N/A';
  const title = !placeholderRemark ? remark : taskNm !== '' ? taskNm : appLabel;
  return {
    id: String(row.id),
    title,
    appId: row.appId,
    appName: appLabel,
    date: row.date,
    hours: Number(row.screenHours) || 0,
    timeRange: unreportedCreatedAtTimeLabel(row.createdAt),
  };
}

/** GET /project-timesheet/submit-trail — normalize camelCase or snake_case rows. */
function mapSubmitTrailApiRow(raw: unknown): ProjectTimesheetSubmitTrailRow | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const user =
    r['user'] != null && typeof r['user'] === 'object' && !Array.isArray(r['user'])
      ? (r['user'] as Record<string, unknown>)
      : null;
  const approverUser =
    r['approverUser'] != null && typeof r['approverUser'] === 'object' && !Array.isArray(r['approverUser'])
      ? (r['approverUser'] as Record<string, unknown>)
      : null;
  const id = Number(r['id']);
  if (!Number.isFinite(id)) return null;
  const submissionBatchId = String(r['submissionBatchId'] ?? r['submission_batch_id'] ?? '').trim();
  const userId = Number(r['userId'] ?? r['user_id'] ?? user?.['userId'] ?? user?.['user_id']);
  const submitterName = String(
    r['submitterName'] ??
      r['submitter_name'] ??
      user?.['userName'] ??
      user?.['user_name'] ??
      user?.['name'] ??
      '',
  ).trim();
  const projectId = Number(r['projectId'] ?? r['project_id']);
  const type = String(r['type'] ?? '').trim() || 'RA';
  const trailEvent = String(r['trailEvent'] ?? r['trail_event'] ?? '').trim();
  const periodStart = String(r['periodStart'] ?? r['period_start'] ?? '').trim();
  const periodEnd = String(r['periodEnd'] ?? r['period_end'] ?? '').trim();
  const approverRaw =
    r['approverUserId'] ?? r['approver_user_id'] ?? approverUser?.['userId'] ?? approverUser?.['user_id'];
  const approverUserId =
    approverRaw != null && approverRaw !== '' ? Number(approverRaw) : Number.NaN;
  const timesheetCount = Number(r['timesheetCount'] ?? r['timesheet_count']);
  const wRaw = r['workplanTimesheetIds'] ?? r['workplan_timesheet_ids'];
  const workplanTimesheetIds = Array.isArray(wRaw)
    ? wRaw.map((x) => Number(x)).filter((n) => Number.isFinite(n))
    : [];
  const rem = r['remarkSnapshot'] ?? r['remark_snapshot'];
  const remarkSnapshot = rem == null || rem === '' ? null : String(rem);
  const createdAt = String(r['createdAt'] ?? r['created_at'] ?? '').trim();
  const updatedAt = String(r['updatedAt'] ?? r['updated_at'] ?? '').trim();
  return {
    id,
    submissionBatchId,
    ...(trailEvent ? { trailEvent } : {}),
    userId: Number.isFinite(userId) ? userId : 0,
    ...(submitterName ? { submitterName } : {}),
    projectId: Number.isFinite(projectId) ? projectId : 0,
    type,
    periodStart,
    periodEnd,
    approverUserId: Number.isFinite(approverUserId) ? approverUserId : 0,
    timesheetCount: Number.isFinite(timesheetCount) ? timesheetCount : workplanTimesheetIds.length,
    workplanTimesheetIds,
    remarkSnapshot,
    createdAt,
    updatedAt,
  };
}

function readPmProjectDisplayMeta(): { name: string; color: string } {
  try {
    const raw = localStorage.getItem('projectDetails');
    if (!raw) return { name: 'Project', color: '#4a90e2' };
    const pd = JSON.parse(raw) as {
      name?: string;
      projectName?: string;
      sector?: { colorCode?: string };
    };
    const name = (pd.name ?? pd.projectName ?? 'Project').trim() || 'Project';
    const color = (pd.sector?.colorCode ?? '').trim() || '#4a90e2';
    return { name, color };
  } catch {
    return { name: 'Project', color: '#4a90e2' };
  }
}

/** Flatten GET user-project-task-board `data.phases` into PM assigned-task rows. */
function mapUserProjectTaskBoardToAssignedTasks(
  phases: unknown,
  projectId: string,
  projectName: string,
  projectColor: string,
): ApiAssignedTask[] {
  if (!Array.isArray(phases)) return [];
  const out: ApiAssignedTask[] = [];
  for (const p of phases) {
    const pr = p as Record<string, unknown>;
    const phaseName = String(pr['phaseName'] ?? pr['phase'] ?? '').trim() || '—';
    const milestones = pr['milestones'];
    if (!Array.isArray(milestones)) continue;
    for (const m of milestones) {
      const mr = m as Record<string, unknown>;
      const milestoneName = String(mr['milestoneName'] ?? mr['milestone'] ?? '').trim() || '—';
      const tasks = mr['tasks'];
      if (!Array.isArray(tasks)) continue;
      for (const t of tasks) {
        const tr = t as Record<string, unknown>;
        const taskIdRaw = tr['taskId'] ?? tr['id'];
        const taskName = String(tr['taskName'] ?? tr['name'] ?? '').trim() || '—';
        const planned = Number(tr['assignedHours'] ?? 0) || 0;
        const startDate = String(tr['startDate'] ?? mr['startDate'] ?? pr['startDate'] ?? '').trim();
        const endDate = String(
          tr['endDate'] ?? tr['dueDate'] ?? mr['dueDate'] ?? mr['endDate'] ?? pr['dueDate'] ?? '',
        ).trim();
        out.push({
          taskId: taskIdRaw != null ? String(taskIdRaw) : '',
          task: taskName,
          projectId,
          projectName,
          projectColor,
          stage: phaseName,
          milestone: milestoneName,
          plannedHours: planned,
          startDate,
          endDate,
          isBillable: resolveBillableFromTaskPayload(tr),
        });
      }
    }
  }
  return out;
}

const AVATAR_PALETTE = ['#3730A3', '#5B21B6', '#065F46', '#B45309', '#0E7490', '#BE185D', '#1D4ED8'] as const;

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0]![0] + parts[parts.length - 1]![0]).toUpperCase();
  }
  if (parts.length === 1 && parts[0]!.length >= 2) {
    return parts[0]!.slice(0, 2).toUpperCase();
  }
  return (name.slice(0, 2) || '?').toUpperCase();
}

function readSubmitterId(s: ManagerTimesheetSubmitterDto & Record<string, unknown>): string | null {
  const v =
    s.userId ??
    s.user_id ??
    s['UserId'] ??
    s['userID'] ??
    s['id'] ??
    s['Id'];
  if (v == null || v === '') return null;
  const str = String(v).trim();
  return str.length ? str : null;
}

function readString(s: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const k of keys) {
    const v = s[k];
    if (typeof v === 'string' && v.trim()) return v;
  }
  return undefined;
}

function mapSubmitterToApiEmployee(s: ManagerTimesheetSubmitterDto): ApiEmployee {
  const rec = s as ManagerTimesheetSubmitterDto & Record<string, unknown>;
  const idRaw = readSubmitterId(rec);
  const uidNum = idRaw != null ? Number(idRaw) : NaN;
  const name =
    (s.name ?? readString(rec, 'Name', 'fullName', 'FullName') ?? '').trim() ||
    (idRaw != null ? `User ${idRaw}` : 'User');
  const colorIdx =
    Number.isFinite(uidNum) && uidNum !== 0
      ? Math.abs(Math.trunc(uidNum)) % AVATAR_PALETTE.length
      : Math.abs(hashString(idRaw ?? name)) % AVATAR_PALETTE.length;
  const approvalStatus =
    s.approvalStatus ??
    readString(rec, 'ApprovalStatus', 'approval_status', 'timesheetStatus', 'TimesheetStatus');
  const totalLoggedHours =
    s.totalLoggedHours ??
    (rec['TotalLoggedHours'] as number | undefined) ??
    (rec['total_logged_hours'] as number | undefined);
  const distinctTaskCount =
    s.distinctTaskCount ??
    (rec['DistinctTaskCount'] as number | undefined) ??
    (rec['distinct_task_count'] as number | undefined);
  const profilePic =
    (typeof s.profilePic === 'string' && s.profilePic.trim()) ||
    readString(rec, 'profilePic', 'ProfilePic', 'profile_pic') ||
    '';
  return {
    id: idRaw ?? '0',
    name,
    initials: initialsFromName(name),
    avatarColor: AVATAR_PALETTE[colorIdx]!,
    role: 'Team member',
    pendingDaysCount: 0,
    ...(approvalStatus != null && approvalStatus !== '' ? { approvalStatus } : {}),
    ...(totalLoggedHours != null && Number.isFinite(Number(totalLoggedHours))
      ? { totalLoggedHours: Number(totalLoggedHours) }
      : {}),
    ...(distinctTaskCount != null && Number.isFinite(Number(distinctTaskCount))
      ? { distinctTaskCount: Number(distinctTaskCount) }
      : {}),
    ...(profilePic ? { profilePicUrl: profilePic } : {}),
  };
}

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return h;
}

function employeeRowsFromPayload(res: unknown): unknown[] {
  if (res == null || typeof res !== 'object') return [];
  const top = res as Record<string, unknown>;
  const data = top['data'];
  const root =
    data != null && typeof data === 'object'
      ? (data as Record<string, unknown>)
      : top;
  const inner = root['data'];
  const bag = inner != null && typeof inner === 'object' ? (inner as Record<string, unknown>) : root;
  const list = bag['employees'] ?? bag['Employees'];
  return Array.isArray(list) ? list : [];
}

function pickSubmitter(row: unknown): ManagerTimesheetSubmitterDto | null {
  if (row == null || typeof row !== 'object') return null;
  const r = row as Record<string, unknown>;
  const sub = r['submitter'] ?? r['Submitter'];
  if (sub != null && typeof sub === 'object') return sub as ManagerTimesheetSubmitterDto;
  if (
    r['userId'] != null ||
    r['user_id'] != null ||
    r['UserId'] != null ||
    r['name'] != null ||
    r['Name'] != null
  ) {
    return row as ManagerTimesheetSubmitterDto;
  }
  return null;
}

function mapManagerTimesheetsByRangeResponse(res: ManagerTimesheetsByRangeResponse): ApiEmployee[] {
  const rows = employeeRowsFromPayload(res);
  const out: ApiEmployee[] = [];
  for (const row of rows) {
    const sub = pickSubmitter(row);
    if (sub == null) continue;
    const idRaw = readSubmitterId(sub as ManagerTimesheetSubmitterDto & Record<string, unknown>);
    if (idRaw == null || idRaw === '0') continue;
    out.push(mapSubmitterToApiEmployee(sub));
  }
  return out;
}

/** GET support-project-timesheets/reporting-manager-by-range — flexible payload shapes. */
function reportingManagerEmployeeListFromResponse(res: unknown): unknown[] {
  if (res == null || typeof res !== 'object') return [];
  const top = res as Record<string, unknown>;
  if (Array.isArray(top['data'])) return top['data'] as unknown[];
  const data = top['data'];
  const root =
    data != null && typeof data === 'object' && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : top;
  for (const key of [
    'employees',
    'Employees',
    'reportees',
    'Reportees',
    'teamMembers',
    'TeamMembers',
    'members',
    'Members',
  ]) {
    const list = root[key];
    if (Array.isArray(list)) return list;
  }
  return [];
}

function mapPlainReporteeRow(row: unknown): ApiEmployee | null {
  const r = asRecord(row);
  if (!r) return null;
  const idRaw = pickStr(
    r,
    'userId',
    'user_id',
    'UserId',
    'employeeId',
    'employee_id',
    'memberId',
    'member_id',
    'id',
    'Id',
  );
  if (!idRaw) return null;
  const id = idRaw.trim();
  if (!id || id === '0') return null;
  let name = pickStr(
    r,
    'name',
    'Name',
    'fullName',
    'FullName',
    'userName',
    'user_name',
    'displayName',
    'DisplayName',
  );
  if (!name) {
    const fn = pickStr(r, 'firstName', 'FirstName', 'givenName', 'GivenName');
    const ln = pickStr(r, 'lastName', 'LastName', 'familyName', 'FamilyName', 'surname', 'Surname');
    name = [fn, ln].filter(Boolean).join(' ').trim();
  }
  if (!name) name = `User ${id}`;
  const role =
    pickStr(
      r,
      'designation',
      'Designation',
      'role',
      'Role',
      'jobTitle',
      'JobTitle',
      'position',
      'Position',
    ) || 'Team member';
  const uidNum = Number(id);
  const colorIdx =
    Number.isFinite(uidNum) && uidNum !== 0
      ? Math.abs(Math.trunc(uidNum)) % AVATAR_PALETTE.length
      : Math.abs(hashString(id)) % AVATAR_PALETTE.length;
  return {
    id,
    name,
    initials: initialsFromName(name),
    avatarColor: AVATAR_PALETTE[colorIdx]!,
    role,
    pendingDaysCount: 0,
  };
}

function dedupeEmployeesById(list: ApiEmployee[]): ApiEmployee[] {
  const seen = new Set<string>();
  const out: ApiEmployee[] = [];
  for (const e of list) {
    if (seen.has(e.id)) continue;
    seen.add(e.id);
    out.push(e);
  }
  return out;
}

function mergeApiEmployeePreferDetail(a: ApiEmployee, b: ApiEmployee): ApiEmployee {
  return {
    ...a,
    name: a.name.startsWith('User ') && !b.name.startsWith('User ') ? b.name : a.name,
    profilePicUrl: a.profilePicUrl?.trim() || b.profilePicUrl?.trim() || undefined,
    approvalStatus: b.approvalStatus?.trim() || a.approvalStatus?.trim() || undefined,
    totalLoggedHours:
      b.totalLoggedHours != null && Number.isFinite(b.totalLoggedHours)
        ? Number(b.totalLoggedHours)
        : a.totalLoggedHours,
    distinctTaskCount:
      b.distinctTaskCount != null && Number.isFinite(b.distinctTaskCount)
        ? Number(b.distinctTaskCount)
        : a.distinctTaskCount,
  };
}

/**
 * Support reporting-manager-by-range: `data.projects[]` + `data.directReportUserIds`.
 * Returns null when `data` is missing or neither shape is present (fall back to legacy parsers).
 */
function parseReportingManagerSupportProjectsPayload(res: unknown): ReportingManagerByRangeParsed | null {
  const top = asRecord(res);
  if (!top) return null;
  const data = asRecord(top['data']);
  if (!data) return null;
  const hasProjectsArr = Array.isArray(data['projects'] ?? data['Projects']);
  const hasDirectArr = Array.isArray(data['directReportUserIds'] ?? data['DirectReportUserIds']);
  if (!hasProjectsArr && !hasDirectArr) return null;

  const empToProjectIds: Record<string, string[]> = {};
  const byEmpId = new Map<string, ApiEmployee>();
  const projectRows: ReportingManagerProjectRowDto[] = [];
  const orderIds: string[] = [];
  const pushOrder = (id: string) => {
    const x = id.trim();
    if (!x || x === '0') return;
    if (!orderIds.includes(x)) orderIds.push(x);
  };

  const addPid = (empId: string, pid: string) => {
    const eid = empId.trim();
    const p = pid.trim();
    if (!eid || !p) return;
    if (!empToProjectIds[eid]) empToProjectIds[eid] = [];
    if (!empToProjectIds[eid].includes(p)) empToProjectIds[eid].push(p);
  };

  const rawProjects = asArray(data['projects'] ?? data['Projects']);
  /** When `projects` is present on the payload, list only people returned under `employees[].submitter` — not `directReportUserIds` placeholders (`User 153`, …). */
  const submittersOnly = hasProjectsArr;
  for (const pr of rawProjects) {
    const prec = asRecord(pr);
    if (!prec) continue;
    const pid = pickStr(prec, 'projectId', 'project_id', 'ProjectId');
    if (!pid) continue;
    const projectCode =
      pickStr(prec, 'projectCode', 'project_code', 'ProjectCode', 'code') || pid;
    const slots: ReportingManagerProjectSlotDto[] = [];
    for (const row of asArray(prec['employees'] ?? prec['Employees'])) {
      const r = asRecord(row);
      if (!r) continue;
      const subRaw = r['submitter'] ?? r['Submitter'];
      if (subRaw == null || typeof subRaw !== 'object') continue;
      const emp = mapSubmitterToApiEmployee(subRaw as ManagerTimesheetSubmitterDto);
      if (!emp.id || emp.id === '0') continue;
      addPid(emp.id, pid);
      const prev = byEmpId.get(emp.id);
      byEmpId.set(emp.id, prev ? mergeApiEmployeePreferDetail(prev, emp) : emp);
      pushOrder(emp.id);
      slots.push({
        userId: emp.id,
        name: emp.name,
        approvalStatus: emp.approvalStatus,
        totalLoggedHours: emp.totalLoggedHours,
        distinctTaskCount: emp.distinctTaskCount,
        profilePicUrl: emp.profilePicUrl,
      });
    }
    if (slots.length) projectRows.push({ projectId: pid, projectCode, slots });
  }

  if (!submittersOnly) {
    for (const id of asArray(data['directReportUserIds'] ?? data['DirectReportUserIds'])) {
      const sid = String(id).trim();
      if (!sid || sid === '0') continue;
      pushOrder(sid);
      if (!byEmpId.has(sid)) {
        const plain = mapPlainReporteeRow({ userId: sid, UserId: sid } as unknown);
        if (plain) byEmpId.set(sid, plain);
      }
    }
  }

  const employees: ApiEmployee[] = [];
  for (const id of orderIds) {
    const e = byEmpId.get(id);
    if (e) employees.push(e);
  }
  return { employees, empToProjectIds, projectRows };
}

function mapReportingManagerRangeResponseLegacy(res: unknown): ApiEmployee[] {
  const viaManagerShape = mapManagerTimesheetsByRangeResponse(res as ManagerTimesheetsByRangeResponse);
  if (viaManagerShape.length > 0) return dedupeEmployeesById(viaManagerShape);
  const rows = reportingManagerEmployeeListFromResponse(res);
  const out: ApiEmployee[] = [];
  for (const row of rows) {
    const sub = pickSubmitter(row);
    if (sub != null) {
      const rec = sub as ManagerTimesheetSubmitterDto & Record<string, unknown>;
      const idRaw = readSubmitterId(rec);
      if (idRaw != null && idRaw !== '' && idRaw !== '0') {
        out.push(mapSubmitterToApiEmployee(sub));
        continue;
      }
    }
    const plain = mapPlainReporteeRow(row);
    if (plain) out.push(plain);
  }
  return dedupeEmployeesById(out);
}

/** Employees only — used where extras are not needed. */
function mapReportingManagerRangeResponse(res: unknown): ApiEmployee[] {
  const support = parseReportingManagerSupportProjectsPayload(res);
  if (support && support.employees.length > 0) return dedupeEmployeesById(support.employees);
  return mapReportingManagerRangeResponseLegacy(res);
}

function parseReportingManagerByRangeComplete(res: unknown): ReportingManagerByRangeParsed {
  const support = parseReportingManagerSupportProjectsPayload(res);
  if (support && (support.employees.length > 0 || support.projectRows.length > 0))
    return {
      employees: dedupeEmployeesById(support.employees),
      empToProjectIds: support.empToProjectIds,
      projectRows: support.projectRows,
    };
  return {
    employees: mapReportingManagerRangeResponseLegacy(res),
    empToProjectIds: {},
    projectRows: [],
  };
}

// ─── POST pending-approval response → entries + per-day statuses ─────────

function asRecord(v: unknown): Record<string, unknown> | null {
  return v != null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function asArray(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

function pickStr(obj: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return '';
}

function pickNum(obj: Record<string, unknown>, ...keys: string[]): number {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim()) {
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
  }
  return 0;
}

/** Positive integer id for timesheet line rows (rejects UUIDs / decimals). */
function pickPositiveIntId(obj: Record<string, unknown>, ...keys: string[]): number | null {
  for (const k of keys) {
    const v = obj[k];
    if (v == null || v === '') continue;
    const n = typeof v === 'number' ? v : Number(String(v).trim());
    if (!Number.isFinite(n) || n <= 0) continue;
    const t = Math.trunc(n);
    if (t !== n) continue;
    return t;
  }
  return null;
}

/**
 * Timesheet detail / line id from a task/line object (nested `detail` / `timesheet_detail` shapes).
 * Do not use CRM `task_id` here — that is shared across activity lines and would collapse distinct entries.
 */
function pickTimesheetDetailIdFromRecord(rec: Record<string, unknown> | null): number | null {
  if (!rec) return null;
  const direct = pickPositiveIntId(
    rec,
    'timesheetDetailId',
    'timesheet_detail_id',
    'TimesheetDetailId',
    'detailId',
    'detail_id',
    'workplanTimesheetDetailId',
    'workplan_timesheet_detail_id',
    'timesheetLineId',
    'timesheet_line_id',
    'lineId',
    'line_id',
    'lineItemId',
    'line_item_id',
    'timesheetRowId',
    'timesheet_row_id',
    'rowId',
    'row_id',
    'entryId',
    'entry_id',
    'timesheetEntryId',
    'timesheet_entry_id',
    /** Persisted line row id (UUIDs fail pickPositiveIntId). Prefer over CRM task id. */
    'id',
    'Id',
  );
  if (direct != null) return direct;
  const nested = asRecord(
    rec['timesheetDetail'] ?? rec['timesheet_detail'] ?? rec['detail'] ?? rec['Detail'] ?? rec['line'],
  );
  if (!nested) return null;
  return pickPositiveIntId(
    nested,
    'id',
    'timesheetDetailId',
    'timesheet_detail_id',
    'detailId',
    'detail_id',
  );
}

function pickStricterDayApprovalStatus(a: ApprovalStatus, b: ApprovalStatus): ApprovalStatus {
  const sev: Record<ApprovalStatus, number> = { rejected: 0, submitted: 1, draft: 2, approved: 3 };
  return sev[a] <= sev[b] ? a : b;
}

function normalizeDayApprovalStatus(raw: string): ApprovalStatus | null {
  const x = raw.toLowerCase().replace(/[\s-]+/g, '_');
  if (x === 'submitted' || x === 'pending' || x === 'submitted_for_approval' || x === 'pending_approval') {
    return 'submitted';
  }
  if (x === 'approved') return 'approved';
  if (x === 'rejected') return 'rejected';
  if (x === 'draft') return 'draft';
  return null;
}

function isoDateSlice(s: string): string {
  const t = s.trim();
  return t.length >= 10 ? t.slice(0, 10) : t;
}

function pickDayDate(d: Record<string, unknown>): string {
  const s = pickStr(d, 'date', 'Date', 'dayDate', 'day_date', 'timesheetDate', 'timesheet_date', 'workDate', 'work_date');
  if (s) return isoDateSlice(s);
  return isoDateSlice(pickStr(d, 'dateStr', 'date_string', 'day'));
}

function unwrapPendingData(res: unknown): Record<string, unknown> | null {
  const top = asRecord(res);
  if (!top) return null;
  const data = top['data'];
  if (data != null && typeof data === 'object' && !Array.isArray(data)) {
    return data as Record<string, unknown>;
  }
  return top;
}

const ENTRY_PROJECT_COLORS = ['#3730A3', '#5B21B6', '#065F46', '#B45309', '#0E7490', '#BE185D', '#1D4ED8'] as const;

function projectColorForPmEntry(projectId: number): string {
  const n = Math.abs(Math.trunc(projectId));
  return ENTRY_PROJECT_COLORS[n % ENTRY_PROJECT_COLORS.length]!;
}


/** `data.by_date` from POST pending-approval (CRM RA shape). */
function parsePendingApprovalByDate(
  byDate: Record<string, unknown>,
  empId: string,
  defaultProjectId: number,
): PendingApprovalTimesheetMapped {
  const entries: ApiTimesheetEntry[] = [];
  const dayStatuses: Record<string, ApprovalStatus> = {};

  const dateKeys = Object.keys(byDate).sort();
  for (const dateKey of dateKeys) {
    const dd = isoDateSlice(dateKey);
    if (!dd || dd.length < 10) continue;

    const blocks = asArray(byDate[dateKey]);
    for (const blockEl of blocks) {
      const block = asRecord(blockEl);
      if (!block) continue;

      const statusRaw = pickStr(block, 'status', 'Status');
      const blockStatus = statusRaw ? normalizeDayApprovalStatus(statusRaw) : null;
      if (blockStatus) {
        const prev = dayStatuses[dd];
        dayStatuses[dd] =
          prev == null ? blockStatus : pickStricterDayApprovalStatus(prev, blockStatus);
      }

      const pid = pickNum(block, 'project_id', 'projectId');
      const projectId = Number.isFinite(pid) && pid !== 0 ? pid : defaultProjectId;
      const projectName = pickStr(block, 'project_name', 'projectName') || 'Project';
      const projectCode = pickStr(block, 'project_code', 'projectCode');
      const projectType = pickStr(block, 'project_type', 'projectType');
      const projColor = projectColorForPmEntry(projectId);
      const blockTimesheetId = pickNum(block, 'timesheet_id', 'timesheetId', 'TimesheetId');

      const activities = asArray(block['activity'] ?? block['Activity']);
      let actIndex = 0;
      for (const actEl of activities) {
        const act = asRecord(actEl);
        if (!act) continue;

        const activityTypeRaw = pickStr(act, 'activity_type', 'activityType') || 'manual';
        const rawLower = activityTypeRaw.toLowerCase();
        const pmActivityType: 'manual' | 'tracked' | 'untracked' =
          rawLower === 'tracked'
            ? 'tracked'
            : rawLower === 'untracked'
              ? 'untracked'
              : 'manual';
        const screenHrs = pickNum(act, 'screen_hours', 'screenHours');
        const isTracked = pmActivityType === 'tracked';
        const appIdRaw = act['app_id'] ?? act['appId'];
        const pmAppId =
          appIdRaw != null && appIdRaw !== '' && Number.isFinite(Number(appIdRaw))
            ? Number(appIdRaw)
            : null;
        const pmAppLabel = pmAppId != null ? timesheetAppDisplayName(pmAppId) : '';
        const tasks = asArray(act['tasks'] ?? act['Tasks']);

        if (tasks.length === 0) {
          actIndex++;
          continue;
        }

        let taskIndex = 0;
        for (const taskEl of tasks) {
          const t = asRecord(taskEl);
          if (!t) continue;
          const taskName = pickStr(t, 'task_name', 'taskName') || '—';
          const logged = pickNum(t, 'logged_hours', 'loggedHours', 'hours');
          const comment = pickStr(t, 'comment', 'Comment');
          const phaseName = resolvePmTaskPhaseName(t);
          const milestoneName = resolvePmTaskMilestoneName(t);
          const phaseDescription = resolvePmTaskPhaseDescription(t);
          const milestoneDescription = resolvePmTaskMilestoneDescription(t);
          const taskIdNum = pickPositiveIntId(t, 'task_id', 'taskId');
          /** Line-unique detail id only — never fall back to activity id when multiple tasks share an activity. */
          const detailNumeric =
            pickTimesheetDetailIdFromRecord(t) ??
            pickPositiveIntId(t, 'taskTimesheetDetailId', 'task_timesheet_detail_id') ??
            (tasks.length === 1 ? pickTimesheetDetailIdFromRecord(act) : null);
          /** Stable row key for the grid (same task_id may appear on multiple activity rows). */
          const rowId = `pa_${empId}_${dd}_${projectId}_a${actIndex}_t${taskIndex}`;
          entries.push({
            id: rowId,
            ...(detailNumeric != null ? { timesheetDetailId: detailNumeric } : {}),
            ...(taskIdNum != null ? { taskId: taskIdNum } : {}),
            ...(blockTimesheetId > 0 ? { timesheetId: blockTimesheetId } : {}),
            empId,
            date: dd,
            projectId: String(projectId),
            projectCode,
            projectName,
            ...(projectCode ? { projectCode } : {}),
            ...(projectType ? { projectType } : {}),
            projectColor: projColor,
            stage: phaseName || '—',
            milestone: milestoneName || '—',
            ...(phaseDescription ? { stageDescription: phaseDescription } : {}),
            ...(milestoneDescription ? { milestoneDescription: milestoneDescription } : {}),
            task: taskName,
            hours: logged,
            isBillable: resolveBillableFromTaskPayload(t),
            isAutoTracked: isTracked,
            aiConfidenceScore: null,
            comment,
            /** Activity-level `screen_hours` — attach once per activity (first task) to avoid double-counting. */
            screenDetectedHours: taskIndex === 0 ? screenHrs : null,
            pmActivityType,
            pmAppId,
            pmAppLabel,
            ...(blockStatus ? { blockApprovalStatus: blockStatus } : {}),
          });
          taskIndex++;
        }
        actIndex++;
      }
    }
  }

  return { entries, dayStatuses };
}

function mapRawLineToApiEntry(
  raw: Record<string, unknown>,
  empId: string,
  dateStr: string,
  idx: number,
  projectIdNum: number,
): ApiTimesheetEntry {
  const detailNumeric = pickTimesheetDetailIdFromRecord(raw);
  const taskIdNum = pickPositiveIntId(raw, 'task_id', 'taskId');
  const rowId = `pa_${empId}_${dateStr}_${idx}`;
  const tsId = pickNum(raw, 'timesheet_id', 'timesheetId');
  const projId = pickStr(raw, 'projectId', 'project_id', 'ProjectId') || String(projectIdNum);
  const pc = pickStr(raw, 'project_code', 'projectCode');
  const pt = pickStr(raw, 'project_type', 'projectType');
  const phaseName = resolvePmTaskPhaseName(raw);
  const milestoneName = resolvePmTaskMilestoneName(raw);
  const phaseDescription = resolvePmTaskPhaseDescription(raw);
  const milestoneDescription = resolvePmTaskMilestoneDescription(raw);
  return {
    id: rowId,
    /** Only real line/detail ids — CRM taskId is sent separately on hourEdits. */
    ...(detailNumeric != null ? { timesheetDetailId: detailNumeric } : {}),
    ...(taskIdNum != null ? { taskId: taskIdNum } : {}),
    ...(tsId > 0 ? { timesheetId: tsId } : {}),
    empId,
    date: dateStr,
    projectId: projId,
    projectName: pickStr(raw, 'projectName', 'project_name', 'ProjectName', 'projectTitle') || 'Project',
    ...(pc ? { projectCode: pc } : {}),
    ...(pt ? { projectType: pt } : {}),
    projectColor: pickStr(raw, 'projectColor', 'project_color', 'color', 'sectorColor') || '#64748B',
    stage: phaseName || pickStr(raw, 'stage', 'stageName', 'stage_name') || '—',
    milestone: milestoneName || pickStr(raw, 'milestone', 'milestoneName', 'milestone_name') || '—',
    ...(phaseDescription ? { stageDescription: phaseDescription } : {}),
    ...(milestoneDescription ? { milestoneDescription: milestoneDescription } : {}),
    task: pickStr(raw, 'task', 'taskName', 'task_name', 'description', 'activityName', 'title') || '—',
    hours: pickNum(raw, 'hours', 'hrs', 'loggedHours', 'logged_hours', 'totalHours', 'effectiveHours'),
    isBillable: resolveBillableFromTaskPayload(raw),
    isAutoTracked: Boolean(raw['isAutoTracked'] ?? raw['auto_tracked'] ?? raw['is_auto_tracked'] ?? false),
    aiConfidenceScore: null,
    comment: pickStr(raw, 'comment', 'Comment', 'remark', 'notes', 'description'),
    screenDetectedHours:
      raw['screenDetectedHours'] != null ||
      raw['screen_hours'] != null ||
      raw['screenHours'] != null
        ? pickNum(raw, 'screenDetectedHours', 'screen_hours', 'screenHours')
        : null,
  };
}

function parsePendingApprovalTimesheetResponse(
  res: unknown,
  empId: string,
  projectIdNum: number,
): PendingApprovalTimesheetMapped {
  const entries: ApiTimesheetEntry[] = [];
  const dayStatuses: Record<string, ApprovalStatus> = {};
  const root = unwrapPendingData(res);
  if (!root) return { entries, dayStatuses };

  const byDateRaw = root['by_date'] ?? root['byDate'];
  if (byDateRaw != null && typeof byDateRaw === 'object' && !Array.isArray(byDateRaw)) {
    return parsePendingApprovalByDate(byDateRaw as Record<string, unknown>, empId, projectIdNum);
  }

  const dayList = asArray(root['days'] ?? root['Days'] ?? root['daily'] ?? root['byDate'] ?? root['dayWise']);
  if (dayList.length > 0) {
    for (const dayEl of dayList) {
      const d = asRecord(dayEl);
      if (!d) continue;
      const dateStr = pickDayDate(d);
      if (!dateStr) continue;
      const statusRaw = pickStr(d, 'status', 'approvalStatus', 'ApprovalStatus', 'timesheetStatus', 'TimesheetStatus');
      if (statusRaw) {
        const st = normalizeDayApprovalStatus(statusRaw);
        if (st) dayStatuses[dateStr] = st;
      }
      const lines = asArray(
        d['entries'] ??
          d['Entries'] ??
          d['tasks'] ??
          d['Tasks'] ??
          d['lines'] ??
          d['items'] ??
          d['activities'] ??
          d['Activities'],
      );
      let i = 0;
      for (const line of lines) {
        const r = asRecord(line);
        if (!r) continue;
        const lineDate = pickStr(r, 'date', 'Date', 'workDate', 'timesheetDate') || dateStr;
        const dd = lineDate.length >= 10 ? isoDateSlice(lineDate) : dateStr;
        entries.push(mapRawLineToApiEntry(r, empId, dd, i++, projectIdNum));
      }
    }
    return { entries, dayStatuses };
  }

  const flat = asArray(
    root['entries'] ??
      root['Entries'] ??
      root['timesheetEntries'] ??
      root['lines'] ??
      root['tasks'] ??
      root['Tasks'],
  );
  let i = 0;
  for (const line of flat) {
    const r = asRecord(line);
    if (!r) continue;
    const dd = isoDateSlice(pickStr(r, 'date', 'Date', 'workDate', 'timesheetDate', 'day'));
    if (!dd) continue;
    entries.push(mapRawLineToApiEntry(r, empId, dd, i++, projectIdNum));
  }
  return { entries, dayStatuses };
}

function parseYmdDate(s: string): Date | null {
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function toYmdDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function enumerateDateRange(startDate: string, endDate: string): string[] {
  const start = parseYmdDate(startDate);
  const end = parseYmdDate(endDate);
  if (!start || !end || start > end) return [];
  const out: string[] = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  while (cursor <= end) {
    out.push(toYmdDate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}

function mockAvatarColor(userId: string): string {
  const n = Number(userId);
  if (Number.isFinite(n)) return AVATAR_PALETTE[Math.abs(Math.trunc(n)) % AVATAR_PALETTE.length]!;
  return AVATAR_PALETTE[Math.abs(hashString(userId)) % AVATAR_PALETTE.length]!;
}

function mockTeamMembers(): ApiEmployeeListResponse {
  const members: Array<{ id: string; name: string; role: string; approvalStatus: string }> = [
    { id: '101', name: 'Aman Verma', role: 'Architect', approvalStatus: 'submitted' },
    { id: '102', name: 'Neha Singh', role: 'Interior Designer', approvalStatus: 'approved' },
    { id: '103', name: 'Ravi Kumar', role: 'BIM Engineer', approvalStatus: 'rejected' },
  ];
  return {
    employees: members.map((m) => ({
      id: m.id,
      name: m.name,
      initials: initialsFromName(m.name),
      avatarColor: mockAvatarColor(m.id),
      role: m.role,
      pendingDaysCount: 0,
      approvalStatus: m.approvalStatus,
      totalLoggedHours: 0,
      distinctTaskCount: 0,
    })),
  };
}

function mockPendingApprovalTimesheet(body: PendingApprovalPostBody): PendingApprovalTimesheetMapped {
  const dates = enumerateDateRange(body.startDate, body.endDate);
  const entries: ApiTimesheetEntry[] = [];
  const dayStatuses: Record<string, ApprovalStatus> = {};
  const projectName = 'PM Mock Project';
  const projectColor = projectColorForPmEntry(body.projectId);

  for (let i = 0; i < dates.length; i++) {
    const d = dates[i]!;
    const weekday = parseYmdDate(d)?.getDay() ?? 0;
    if (weekday === 0 || weekday === 6) continue;
    if (weekday === 1 || weekday === 2 || weekday === 3) dayStatuses[d] = 'submitted';
    else if (weekday === 4) dayStatuses[d] = 'approved';
    else dayStatuses[d] = 'rejected';

    const mockDetailBase = 8_000_000 + Number(body.userId) * 1_000 + i * 10;
    entries.push({
      id: String(mockDetailBase + 1),
      timesheetDetailId: mockDetailBase + 1,
      timesheetId: 70_000 + i,
      taskId: mockDetailBase + 1,
      empId: String(body.userId),
      date: d,
      projectId: String(body.projectId),
      projectCode: `MOCK-${body.projectId}`,
      projectName,
      projectColor,
      stage: 'Manual',
      milestone: 'Documentation',
      task: `Mock UI Task ${i + 1}`,
      hours: 4,
      isBillable: true,
      isAutoTracked: false,
      aiConfidenceScore: null,
      comment: 'Local PM mock entry',
      pmActivityType: 'manual',
      pmAppId: null,
      pmAppLabel: '',
    });
    entries.push({
      id: String(mockDetailBase + 2),
      timesheetDetailId: mockDetailBase + 2,
      timesheetId: 70_000 + i,
      taskId: mockDetailBase + 2,
      empId: String(body.userId),
      date: d,
      projectId: String(body.projectId),
      projectCode: `MOCK-${body.projectId}`,
      projectName,
      projectColor,
      stage: 'Tracked',
      milestone: 'Execution',
      task: 'Auto tracked coordination',
      hours: 2,
      isBillable: true,
      isAutoTracked: true,
      aiConfidenceScore: null,
      comment: 'Generated from fallback mock',
      screenDetectedHours: 2,
      pmActivityType: 'tracked',
      pmAppId: 5,
      pmAppLabel: timesheetAppDisplayName(5),
    });
  }
  return { entries, dayStatuses };
}

function mockAssignedTasks(projectId: string): ApiAssignedTasksResponse {
  return {
    tasks: [
      {
        taskId: 'mock-task-1',
        task: 'Concept Presentation',
        projectId,
        projectName: 'PM Mock Project',
        projectColor: '#4a90e2',
        stage: 'Concept Design',
        milestone: 'Client Brief',
        plannedHours: 10,
        startDate: '2026-04-14',
        endDate: '2026-04-22',
        isBillable: true,
      },
      {
        taskId: 'mock-task-2',
        task: 'Drawing Coordination',
        projectId,
        projectName: 'PM Mock Project',
        projectColor: '#4a90e2',
        stage: 'Design Development',
        milestone: 'Technical Studies',
        plannedHours: 14,
        startDate: '2026-04-13',
        endDate: '2026-04-26',
        isBillable: false,
      },
    ],
  };
}

function mockUnreportedApps(startDate: string): ApiUnreportedAppsResponse {
  const softwareA = timesheetAppDisplayName(5);
  const softwareB = timesheetAppDisplayName(7);
  return {
    apps: [
      {
        id: 'mock-app-1',
        title: `${softwareA} — Floor Plan View`,
        appId: 5,
        appName: softwareA,
        date: startDate,
        hours: 1.5,
        timeRange: '10:15 AM',
      },
      {
        id: 'mock-app-1b',
        title: `${softwareA} — Section View`,
        appId: 5,
        appName: softwareA,
        date: startDate,
        hours: 0.4,
        timeRange: '11:00 AM',
      },
      {
        id: 'mock-app-2',
        title: `${softwareB} — Design review`,
        appId: 7,
        appName: softwareB,
        date: startDate,
        hours: 0.75,
        timeRange: '03:40 PM',
      },
    ],
  };
}

function mockSubmitTrail(
  projectId: string,
  startDate: string,
  endDate: string,
): ProjectTimesheetSubmitTrailRow[] {
  return [
    {
      id: 900001,
      submissionBatchId: 'mock-batch-1',
      userId: 101,
      projectId: Number(projectId) || 0,
      type: 'RA',
      periodStart: startDate,
      periodEnd: endDate,
      approverUserId: 201,
      timesheetCount: 8,
      workplanTimesheetIds: [701, 702],
      remarkSnapshot: 'Mock trail entry',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  ];
}

@Injectable({ providedIn: 'root' })
export class PmTimesheetApiService {
  private readonly http = inject(HttpClient);

  /**
   * Team list for the employee panel. GET project-timesheet/manager-timesheets-by-range
   * (startDate, endDate, projectId).
   */
  getTeamMembers(projectId: string, startDate: string, endDate: string): Observable<ApiEmployeeListResponse> {
    const pid = String(projectId).trim();
    const params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate)
      .set('projectId', pid);
    return this.http
      .get<ManagerTimesheetsByRangeResponse>(apiUrls.timesheet.get_manager_timesheets_by_range, {
        params,
        responseType: 'json',
      })
      .pipe(
        map((body) => ({ employees: mapManagerTimesheetsByRangeResponse(body) })),
        catchError(() => of({ employees: [] })),
      );
  }

  /**
   * Reporting manager reportee list (support-project timesheets).
   * GET support-project-timesheets/reporting-manager-by-range?startDate=&endDate=
   */
  getReportingManagerReporteesByRange(
    startDate: string,
    endDate: string,
  ): Observable<ApiEmployeeListResponse> {
    const params = new HttpParams().set('startDate', startDate).set('endDate', endDate);
    return this.http
      .get<unknown>(apiUrls.timesheet.get_support_project_reporting_manager_by_range, {
        params,
        responseType: 'json',
      })
      .pipe(
        map((body) => {
          const parsed = parseReportingManagerByRangeComplete(body);
          const mapped: ApiEmployeeListResponse = {
            employees: parsed.employees,
            empToProjectIds: parsed.empToProjectIds,
            reportingManagerProjectRows: parsed.projectRows,
          };
          return mapped;
        }),
        catchError(() => of({ employees: [] })),
      );
  }

  /** GET projects/:projectId/members-status — project member activity status (active/inactive). */
  getProjectMembersStatus(projectId: string): Observable<ProjectMemberStatusRowDto[]> {
    return this.http.get<ProjectMembersStatusApiResponse>(getProjectMembersStatusUrl(projectId)).pipe(
      map((body) => (Array.isArray(body.data) ? body.data : [])),
      catchError(() => of([])),
    );
  }

  /**
   * POST project-timesheet/pending-approval — timesheet lines and optional per-day approval status
   * for the visible range (replaces legacy GET /timesheets/pm/entries when project + user are known).
   */
  postPendingApprovalTimesheet(body: PendingApprovalPostBody): Observable<PendingApprovalTimesheetMapped> {
    return this.http.post<unknown>(apiUrls.timesheet.post_project_timesheet_pending_approval, body).pipe(
      map((res) => parsePendingApprovalTimesheetResponse(res, String(body.userId), body.projectId)),
      catchError(() => of({ entries: [], dayStatuses: {} })),
    );
  }

  /**
   * POST support-project-timesheets/pending-approval — same body shape as project pending-approval;
   * used for Approval Timesheet (reporting manager) weekly grid lines.
   */
  postSupportProjectPendingApprovalTimesheet(body: PendingApprovalPostBody): Observable<PendingApprovalTimesheetMapped> {
    return this.http.post<unknown>(apiUrls.timesheet.post_support_project_timesheet_pending_approval, body).pipe(
      map((res) => parsePendingApprovalTimesheetResponse(res, String(body.userId), body.projectId)),
      catchError(() => of({ entries: [], dayStatuses: {} })),
    );
  }

  /** POST bulk approve/reject for visible range (PM Approve all / Reject all). */
  postProjectTimesheetApprovalStatusByRange(
    body: ProjectTimesheetApprovalStatusByRangeBody,
  ): Observable<TimesheetApprovalMutationResult> {
    return this.http
      .post<ApprovalStatusByRangeApiResponse>(
        apiUrls.timesheet.post_project_timesheet_approval_status_by_range,
        body,
        { headers: { 'Content-Type': 'application/json' } },
      )
      .pipe(
        map((r) => mapApprovalStatusByRangeResponse(r)),
        catchError((err) => of(mapApprovalStatusByRangeHttpError(err))),
      );
  }

  /** POST bulk approve/reject for support-project timesheets (RM Approval tab). */
  postSupportProjectTimesheetApprovalStatusByRange(
    body: SupportProjectTimesheetApprovalStatusByRangeBody,
  ): Observable<TimesheetApprovalMutationResult> {
    return this.http
      .post<ApprovalStatusByRangeApiResponse>(
        apiUrls.timesheet.post_support_project_timesheet_approval_status_by_range,
        body,
        { headers: { 'Content-Type': 'application/json' } },
      )
      .pipe(
        map((r) => mapApprovalStatusByRangeResponse(r)),
        catchError((err) => of(mapApprovalStatusByRangeHttpError(err))),
      );
  }

  getTimesheetEntries(empId: string, from: string, to: string): Observable<ApiTimesheetEntriesResponse> {
    return this.http
      .get<ApiTimesheetEntriesResponse>(`${API_BASE}/entries`, {
        params: { empId, from, to },
      })
      .pipe(catchError(() => of({ entries: [] })));
  }

  approveDay(body: ApiApproveRequest): Observable<ApiApproveResponse> {
    return this.http.post<ApiApproveResponse>(`${API_BASE}/approve`, body);
  }

  rejectDay(body: ApiRejectRequest): Observable<ApiRejectResponse> {
    return this.http.post<ApiRejectResponse>(`${API_BASE}/reject`, body);
  }

  editTimesheet(body: ApiEditTimesheetRequest): Observable<ApiEditTimesheetResponse> {
    return this.http.put<ApiEditTimesheetResponse>(`${API_BASE}/entries`, body);
  }

  /** POST PM manager review endpoint (edit hours + auto-approve). */
  postProjectTimesheetManagerReview(
    projectId: number,
    body: ProjectTimesheetManagerReviewBody,
  ): Observable<boolean> {
    const url = `${apiUrls.timesheet.post_project_timesheet_manager_review}/${projectId}/manager-review`;
    return this.http.post<{ success?: boolean }>(url, body, {
      headers: { 'Content-Type': 'application/json' },
    }).pipe(
      map((r) => {
        if (r == null) return true;
        if (typeof (r as { success?: boolean }).success === 'boolean') {
          return (r as { success: boolean }).success;
        }
        return true;
      }),
    );
  }

  /**
   * GET RA `user-project-task-board?projectId=&userId=` — assigned tasks for PM “Assigned tasks” modal.
   */
  getAssignedTasks(projectId: string, userId: string): Observable<ApiAssignedTasksResponse> {
    const pid = String(projectId).trim();
    const uid = String(userId).trim();
    if (!pid || !uid) return of({ tasks: [] });
    const { name: projectName, color: projectColor } = readPmProjectDisplayMeta();
    const url = apiUrls.resource_allocation.project.user_project_task_board;
    const params = new HttpParams().set('projectId', pid).set('userId', uid);
    return this.http
      .get<{ success?: boolean; data?: { phases?: unknown[] } }>(url, { params })
      .pipe(
        map((res) => {
          const phases = res?.data?.phases;
          if (!res?.success || !Array.isArray(phases)) {
            return { tasks: [] };
          }
          return {
            tasks: mapUserProjectTaskBoardToAssignedTasks(phases, pid, projectName, projectColor),
          };
        }),
        catchError(() => of({ tasks: [] })),
      );
  }

  getUnreportedApps(empId: string, from: string, to: string): Observable<ApiUnreportedAppsResponse> {
    return this.http
      .get<ApiUnreportedAppsResponse>(`${API_BASE}/unreported`, {
        params: { empId, from, to },
      })
      .pipe(catchError(() => of({ apps: [] })));
  }

  /** GET RA project-timesheet unreported rows for a team member (same shape as unreported-entries). */
  getUnreportedEntriesForUser(
    userId: number,
    startDate: string,
    endDate: string,
  ): Observable<ApiUnreportedAppsResponse> {
    const params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate)
      .set('userId', String(userId));
    return this.http
      .get<ProjectTimesheetUnreportedEntriesResponse>(
        apiUrls.timesheet.get_project_timesheet_unreported_entries_for_user,
        { params },
      )
      .pipe(
        map((res) => {
          if (!res?.success || !Array.isArray(res.data)) {
            return { apps: [] };
          }
          return { apps: res.data.map(mapProjectUnreportedRowToPmApp) };
        }),
        catchError(() => of({ apps: [] })),
      );
  }

  /**
   * GET /project-timesheet/submit-trail — approval trail (submission batches) for the project.
   * Same `projectId` query string as manager-timesheets-by-range.
   */
  getSubmitTrail(projectId: string): Observable<ProjectTimesheetSubmitTrailRow[]> {
    const params = new HttpParams().set('projectId', String(projectId).trim());
    return this.http.get<unknown>(apiUrls.timesheet.get_project_timesheet_submit_trail, { params }).pipe(
      map((body) => {
        const res = body as Partial<ProjectTimesheetSubmitTrailResponse>;
        if (!res?.success || !Array.isArray(res.data)) {
          return [];
        }
        return res.data
          .map(mapSubmitTrailApiRow)
          .filter((x): x is ProjectTimesheetSubmitTrailRow => x != null);
      }),
      catchError(() => of([])),
    );
  }
}